import {
  setPrincipleQuestion,
  setPrincipleQuestions,
  setPrincipleQuestionsSearchResults,
} from "reduxStore/state";

const BASE_URL = process.env.REACT_APP_BASE_URL;

// Fetch all principle questions
export const fetchPrincipleQuestions =
  ({ page = 1, limit = 10, sort, search, location, principle }) =>
  async (dispatch, getState) => {
    try {
      const token = getState().token;

      const locationQuery = new URLSearchParams({
        ...(page && { page }),
        ...(limit && { limit }),
        ...(sort && { sort }),
        ...(search && { search }),
        ...(location && { location }),
        ...(principle && { principle }),
      }).toString();

      const response = await fetch(
        `${BASE_URL}/principleQuestion?${locationQuery}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) throw new Error("Failed to fetch principle questions");

      const data = await response.json();
      dispatch(setPrincipleQuestions({ data: data.data, total: data.total }));
      return data;
    } catch (error) {
      console.error("Error while fetching principle questions:", error);
      return { error: error.message };
    }
  };

// Fetch all active principle questions
export const fetchActivePrincipleQuestions =
  (location) => async (dispatch, getState) => {
    try {
      const token = getState().token;

      const response = await fetch(
        `${BASE_URL}/principleQuestion/active/${location}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok)
        throw new Error("Failed to fetch active principle questions");

      const principleQuestions = await response.json();

      dispatch(setPrincipleQuestions({ data: principleQuestions }));
      return principleQuestions;
    } catch (error) {
      console.error("Error while fetching active principle questions:", error);
      return { error: error.message };
    }
  };

// Fetch a single principle question by ID
export const fetchPrincipleQuestion = (id) => async (dispatch, getState) => {
  try {
    const token = getState().token;

    const response = await fetch(`${BASE_URL}/principleQuestion/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) throw new Error("Failed to fetch principle question");

    const principleQuestion = await response.json();
    dispatch(setPrincipleQuestion(principleQuestion));
    return principleQuestion;
  } catch (error) {
    console.error("Error while fetching principle question:", error);
    return { error: error.message };
  }
};

// Create a new principle question
export const createPrincipleQuestion = (data) => async (dispatch, getState) => {
  try {
    const token = getState().token;

    const response = await fetch(`${BASE_URL}/principleQuestion`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw new Error(
        responseData.message || "Failed to create principle question"
      );
    }

    return responseData; // Return successful response
  } catch (error) {
    console.error("Error while creating principle question:", error);
    return {
      error: true,
      message: error.message || "Unexpected error occurred.",
    };
  }
};

// Update a principle question by ID
export const updatePrincipleQuestion =
  (id, data) => async (dispatch, getState) => {
    try {
      // console.log("🚀 ~ data:", data);
      const token = getState().token;

      const response = await fetch(`${BASE_URL}/principleQuestion/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) throw new Error("Failed to update principle question");

      const principleQuestion = await response.json();
      dispatch(setPrincipleQuestion(principleQuestion));
      return principleQuestion;
    } catch (error) {
      console.error("Error while updating principle question:", error);
      return { error: error.message };
    }
  };

// Delete a principle question by ID
export const deletePrincipleQuestion =
  (id, locationId) => async (dispatch, getState) => {
    try {
      const token = getState().token;

      const response = await fetch(`${BASE_URL}/principleQuestion/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const responseData = await response.json();
      if (!response.ok)
        throw new Error(
          responseData.message || "Failed to delete principle question"
        );

      return { success: true, message: responseData.message };
    } catch (error) {
      console.error("Error while deleting principle question:", error);
      return { success: false, message: error.message };
    }
  };

// Search principle questions
export const searchPrincipleQuestions =
  (search) => async (dispatch, getState) => {
    try {
      const token = getState().token;

      const response = await fetch(
        `${BASE_URL}/principleQuestion/search?search=${search}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) throw new Error("Failed to search principle questions");

      const principleQuestions = await response.json();
      dispatch(setPrincipleQuestionsSearchResults(principleQuestions));
      return principleQuestions;
    } catch (error) {
      console.error("Error while searching principle questions:", error);
      return { error: error.message };
    }
  };

// Copy a principle question to multiple locations
export const copyPrincipleQuestion =
  (id, newLocations, newOrganisation) => async (dispatch, getState) => {
    try {
      const token = getState().token;

      const response = await fetch(`${BASE_URL}/principleQuestion/copy/${id}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ newLocations, newOrganisation }),
      });

      const responseData = await response.json();

      if (!response.ok || !responseData) {
        throw new Error(
          responseData.message || "Failed to copy principle question"
        );
      }

      return responseData; // Ensure it always returns a valid object
    } catch (error) {
      console.error("Error while copying principle question:", error);
      return {
        error: true,
        message: error.message || "Unexpected error occurred.",
        failedLocations: [],
        duplicateLocations: [],
      };
    }
  };
